import React, { useEffect, useState } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { DynamicActivityDefinition, IfActivitySettings, Variable } from '../types'
import { ACTIVITY_TYPES } from '../helpers'

interface AdvancedIfSettingsProps {
  selectedStep: any
  showSettings: boolean
  onSave: (parameters: IfActivitySettings, variables: Variable[]) => void
  onCancel: () => void
  workflowVariables?: Variable[]
}

const AdvancedIfSettings: React.FC<AdvancedIfSettingsProps> = ({
  selectedStep,
  showSettings,
  onSave,
  onCancel,
  workflowVariables = []
}) => {
  const [condition, setCondition] = useState(selectedStep?.settings?.condition || '')
  const [available, setAvailable] = useState(selectedStep?.settings?.isAvailable || '')
  const [conditionType, setConditionType] = useState(selectedStep?.settings?.conditionType || 'Variable')
  const [thenActivities, setThenActivities] = useState<DynamicActivityDefinition[]>(
    selectedStep?.settings?.thenActivities || []
  )
  const [elseActivities, setElseActivities] = useState<DynamicActivityDefinition[]>(
    selectedStep?.settings?.elseActivities || []
  )

  useEffect(() => {
    if (selectedStep) {
      setCondition(selectedStep.settings.condition || '')
      setAvailable(selectedStep.settings.isAvailable || '')
      setConditionType(selectedStep.settings.conditionType || 'Variable')
      setThenActivities(selectedStep.settings.thenActivities || [])
      setElseActivities(selectedStep.settings.elseActivities || [])
    }
  }, [selectedStep])

  if (!showSettings || !selectedStep) return null

  const addActivity = (branch: 'then' | 'else') => {
    const newActivity: DynamicActivityDefinition = {
      type: ACTIVITY_TYPES.WRITE_LINE,
      name: `${branch === 'then' ? 'Then' : 'Else'} Activity ${branch === 'then' ? thenActivities.length + 1 : elseActivities.length + 1}`,
      parameters: { message: `Default message for ${branch} branch` }
    }

    if (branch === 'then') {
      setThenActivities([...thenActivities, newActivity])
    } else {
      setElseActivities([...elseActivities, newActivity])
    }
  }

  const removeActivity = (branch: 'then' | 'else', index: number) => {
    if (branch === 'then') {
      setThenActivities(thenActivities.filter((_, i) => i !== index))
    } else {
      setElseActivities(elseActivities.filter((_, i) => i !== index))
    }
  }

  const updateActivity = (branch: 'then' | 'else', index: number, field: string, value: any) => {
    if (branch === 'then') {
      const updated = [...thenActivities]
      if (field === 'type') {
        updated[index].type = value
        // Reset parameters and update name when type changes
        updated[index].parameters = getDefaultParameters(value)
        updated[index].name = getDefaultActivityName(value, branch, index + 1)
      } else if (field === 'name') {
        updated[index].name = value
      } else {
        updated[index].parameters = { ...updated[index].parameters, [field]: value }
      }
      setThenActivities(updated)
    } else {
      const updated = [...elseActivities]
      if (field === 'type') {
        updated[index].type = value
        // Reset parameters and update name when type changes
        updated[index].parameters = getDefaultParameters(value)
        updated[index].name = getDefaultActivityName(value, branch, index + 1)
      } else if (field === 'name') {
        updated[index].name = value
      } else {
        updated[index].parameters = { ...updated[index].parameters, [field]: value }
      }
      setElseActivities(updated)
    }
  }

  const getDefaultParameters = (activityType: string) => {
    switch (activityType) {
      case ACTIVITY_TYPES.WRITE_LINE:
        return { message: 'Default message' }
      case ACTIVITY_TYPES.DELAY:
        return { duration: '00:00:05' }
      case ACTIVITY_TYPES.SEND_EMAIL_MARKETING:
        return { recipientEmail: '', recipientName: '' }
      case ACTIVITY_TYPES.READ_LINE:
        return { prompt: 'Enter input' }
      default:
        return {}
    }
  }

  const getDefaultActivityName = (activityType: string, branch: 'then' | 'else', index: number) => {
    const branchPrefix = branch === 'then' ? 'Then' : 'Else'
    switch (activityType) {
      case ACTIVITY_TYPES.WRITE_LINE:
        return `${branchPrefix} Write Message ${index}`
      case ACTIVITY_TYPES.DELAY:
        return `${branchPrefix} Wait ${index}`
      case ACTIVITY_TYPES.SEND_EMAIL_MARKETING:
        return `${branchPrefix} Send Email ${index}`
      case ACTIVITY_TYPES.READ_LINE:
        return `${branchPrefix} Read Input ${index}`
      case ACTIVITY_TYPES.SEND_MESSAGE_REMINDER:
        return `${branchPrefix} Send Message Reminder ${index}`
      default:
        return `${branchPrefix} Activity ${index}`
    }
  }

  const handleSave = () => {
    const parameters: IfActivitySettings = {
      condition,
      conditionType: conditionType as 'Literal' | 'Variable' | 'Expression',
      thenActivities: thenActivities.length > 0 ? thenActivities : undefined,
      elseActivities: elseActivities.length > 0 ? elseActivities : undefined,
    }

    onSave(parameters, []) // No variables managed here anymore
  }

  const renderActivityForm = (activity: DynamicActivityDefinition, branch: 'then' | 'else', index: number) => (
    <div key={index} className="border border-slate-200 dark:border-zink-600 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex-1 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Activity Type
            </label>
            <select
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.type}
              onChange={(e) => updateActivity(branch, index, 'type', e.target.value)}
            >
              <option value={ACTIVITY_TYPES.WRITE_LINE}>Write Line</option>
              <option value={ACTIVITY_TYPES.DELAY}>Delay</option>
              <option value={ACTIVITY_TYPES.SEND_EMAIL_MARKETING}>Send Email</option>
              <option value={ACTIVITY_TYPES.READ_LINE}>Read Line</option>
              <option value={ACTIVITY_TYPES.SEND_MESSAGE_REMINDER}>Send Message Reminder</option>
              <option value={ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST}>Send HTTP Request</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Name
            </label>
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.name || ''}
              onChange={(e) => updateActivity(branch, index, 'name', e.target.value)}
              placeholder="Activity name"
            />
          </div>
        </div>
        <button
          onClick={() => removeActivity(branch, index)}
          className="ml-2 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Activity-specific parameters */}
      {activity.type === ACTIVITY_TYPES.WRITE_LINE && (
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
            Message
          </label>
          <textarea
            className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
            rows={2}
            value={activity.parameters?.message || ''}
            onChange={(e) => updateActivity(branch, index, 'message', e.target.value)}
            placeholder="Enter message"
          />
        </div>
      )}

      {/* Delay Activity */}
      {activity.type === ACTIVITY_TYPES.DELAY && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Duration
            </label>
            <input
              type="number"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={(() => {
                const duration = activity.parameters?.duration || '00:00:05'
                const parts = duration.split(':')
                const hours = parseInt(parts[0] || '0')
                const minutes = parseInt(parts[1] || '0')
                const seconds = parseInt(parts[2] || '5')

                // Determine which unit is being used based on the non-zero values
                if (hours > 0) return hours.toString()
                if (minutes > 0) return minutes.toString()
                return seconds.toString()
              })()}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1
                const currentDuration = activity.parameters?.duration || '00:00:05'
                const parts = currentDuration.split(':')
                const hours = parseInt(parts[0] || '0')
                const minutes = parseInt(parts[1] || '0')
                const seconds = parseInt(parts[2] || '5')

                let timeSpan = '00:00:05'
                // Determine current unit based on which value is non-zero
                if (hours > 0) {
                  timeSpan = `${value.toString().padStart(2, '0')}:00:00`
                } else if (minutes > 0) {
                  timeSpan = `00:${value.toString().padStart(2, '0')}:00`
                } else {
                  timeSpan = `00:00:${value.toString().padStart(2, '0')}`
                }

                updateActivity(branch, index, 'duration', timeSpan)
              }}
              placeholder="5"
              min="1"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Unit
            </label>
            <select
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={(() => {
                const duration = activity.parameters?.duration || '00:00:05'
                const parts = duration.split(':')
                const hours = parseInt(parts[0] || '0')
                const minutes = parseInt(parts[1] || '0')

                if (hours > 0) return 'hours'
                if (minutes > 0) return 'minutes'
                return 'seconds'
              })()}
              onChange={(e) => {
                const currentDuration = activity.parameters?.duration || '00:00:05'
                const parts = currentDuration.split(':')
                const currentValue = (() => {
                  const hours = parseInt(parts[0] || '0')
                  const minutes = parseInt(parts[1] || '0')
                  const seconds = parseInt(parts[2] || '5')

                  if (hours > 0) return hours
                  if (minutes > 0) return minutes
                  return seconds
                })()

                let timeSpan = '00:00:05'
                switch (e.target.value) {
                  case 'seconds':
                    timeSpan = `00:00:${currentValue.toString().padStart(2, '0')}`
                    break
                  case 'minutes':
                    timeSpan = `00:${currentValue.toString().padStart(2, '0')}:00`
                    break
                  case 'hours':
                    timeSpan = `${currentValue.toString().padStart(2, '0')}:00:00`
                    break
                }
                updateActivity(branch, index, 'duration', timeSpan)
              }}
            >
              <option value="seconds">Seconds</option>
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
            </select>
          </div>
        </div>
      )}

      {/* Send Email Marketing */}
      {activity.type === ACTIVITY_TYPES.SEND_EMAIL_MARKETING && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Recipient Email
            </label>
            <input
              type="email"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.recipientEmail || ''}
              onChange={(e) => updateActivity(branch, index, 'recipientEmail', e.target.value)}
              placeholder="user@example.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
              Recipient Name
            </label>
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
              value={activity.parameters?.recipientName || ''}
              onChange={(e) => updateActivity(branch, index, 'recipientName', e.target.value)}
              placeholder="User Name"
            />
          </div>
        </div>
      )}

      {/* Check Available Slot */}
      {activity.type === ACTIVITY_TYPES.FLOW_SEND_HTTP_REQUEST && (
        <>
          <div className='flex flex-wrap gap-2'>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                Title
              </label>
              <input
                type="text"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.title || ''}
                onChange={(e) => updateActivity(branch, index, 'title', e.target.value)}
                placeholder="Enter title"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                URL
              </label>
              <input
                type="text"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.url || ''}
                onChange={(e) => updateActivity(branch, index, 'url', e.target.value)}
                placeholder="Enter URL"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                Method
              </label>
              <select
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.method || 'GET'}
                onChange={(e) => updateActivity(branch, index, 'method', e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                Content
              </label>
              <input
                type="text"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.content || ''}
                onChange={(e) => updateActivity(branch, index, 'content', e.target.value)}
                placeholder="Enter Content"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                Content Type
              </label>
              <select
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.contentType || 'application/json'}
                onChange={(e) => updateActivity(branch, index, 'contentType', e.target.value)}
              >
                <option value="application/json">JSON</option>
                <option value="application/xml">XML</option>
                <option value="text/plain">Plain Text</option>
              </select>
            </div>
            <div className="flex-1 hidden">
              <label className="block text-xs font-medium text-slate-600 dark:text-zink-300 mb-1">
                Authorization
              </label>
              <input
                type="text"
                className="w-full px-2 py-1 text-sm border border-slate-300 rounded dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                value={activity.parameters?.authorization || `Bearer ${localStorage.getItem('authUser')}`.trim()}
                onChange={(e) => updateActivity(branch, index, 'authorization', e.target.value)}
                placeholder="Enter Authorization"
              />
            </div>
          </div>
        </>
      )}
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="relative bg-white dark:bg-zink-700 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="p-6 border-b border-slate-200 dark:border-zink-600">
          <h3 className="text-15 text-slate-800 dark:text-zink-50">
            Configure If Activity
          </h3>
        </div>
        <span onClick={onCancel} className='absolute top-4 right-4 hover:text-slate-600'><X /></span>

        <div className="p-6 space-y-6">
          {/* Condition for If Activity */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Condition
                </label>
                {conditionType === 'Variable' ? (
                  <select
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                  >
                    <option value="">Select a variable</option>
                    {workflowVariables.map((variable) => (
                      <option key={variable.id} value={variable.name}>
                        {variable.name} ({variable.typeName})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                    placeholder={conditionType === 'Literal' ? 'true or false' : 'Enter expression'}
                  />
                )}
                <p className="text-xs text-slate-500 dark:text-zink-400 mt-1">
                  {conditionType === 'Variable' && 'Select a variable from the dropdown'}
                  {conditionType === 'Literal' && 'Enter true or false'}
                  {conditionType === 'Expression' && 'Enter a condition expression'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-zink-200 mb-1">
                  Condition Type
                </label>
                <select
                  className="w-full px-3 py-2 border border-slate-300 rounded-md dark:border-zink-500 dark:bg-zink-700 dark:text-zink-50"
                  value={conditionType}
                  onChange={(e) => setConditionType(e.target.value)}
                >
                  <option value="Variable">Variable</option>
                  <option value="Literal">Literal (true/false)</option>
                  <option value="Expression">Expression</option>
                </select>
              </div>
            </div>
          </div>

          {/* Then Branch */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm text-slate-700 dark:text-zink-200">Then Branch (True)</h4>
              <button
                onClick={() => addActivity('then')}
                className="px-3 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                Add Activity
              </button>
            </div>
            <div className="space-y-3">
              {thenActivities.map((activity, index) => renderActivityForm(activity, 'then', index))}
              {thenActivities.length === 0 && (
                <div className="text-center py-8 text-slate-500 dark:text-zink-400 border-2 border-dashed border-slate-300 dark:border-zink-600 rounded-lg">
                  No activities in Then branch. Click "Add Activity" to add one.
                </div>
              )}
            </div>
          </div>

          {/* Else Branch */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm text-slate-700 dark:text-zink-200">Else Branch (False)</h4>
              <button
                onClick={() => addActivity('else')}
                className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                Add Activity
              </button>
            </div>
            <div className="space-y-3">
              {elseActivities.map((activity, index) => renderActivityForm(activity, 'else', index))}
              {elseActivities.length === 0 && (
                <div className="text-center py-8 text-slate-500 dark:text-zink-400 border-2 border-dashed border-slate-300 dark:border-zink-600 rounded-lg">
                  No activities in Else branch. Click "Add Activity" to add one.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-slate-200 dark:border-zink-600">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-custom-500 text-white rounded-lg hover:bg-custom-600 transition-colors"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  )
}

export default AdvancedIfSettings
